import { useContext, useEffect, useState } from "react"
import { JSONSchema7 } from "json-schema"
import { isValid, getFormDataForAnnotationContent } from "resha"
import { DocedBoxFeature } from "ramper"
import { OutlinedButton, ValidationError } from "dash-styled"
import { AnnotationFieldContent } from "universal-schema/lib/1.0.0/annotation"
import { ReactComponent as CheckIcon } from "icons/lib/check_circle.svg"

import { useBoolean } from "../../../../../../../../Hooks/useBoolean"
import { AnnotationFieldsStateContext } from "../../../AnnotationFieldsStateContext"
import {
    BoundingBoxStatus,
    BoundingBoxStatusStyles,
    NON_TRUSTED_STYLE,
    ACTIVE_STYLE
} from "../../../constants"
import { InstructionsHeader } from "../Common/InstructionsHeader"
import { ContentInput } from "./ContentInput"

type PrimitiveInstructionsProps = {
    rootSchema: JSONSchema7
    contentSchema: JSONSchema7
    setValidation: (fn: () => void) => void
    setValid: (valid: boolean) => void
    onSubmit: (data: unknown) => void
}

export const PrimitiveInstructions = ({
    rootSchema,
    contentSchema,
    setValidation,
    setValid,
    onSubmit
}: PrimitiveInstructionsProps) => {
    const {
        changed,
        status,
        currentBoundingBoxes,
        currentAnnotations: currentAnnotationsContext,
        rendererHelpers,
        setStatus,
        setCurrentAnnotations,
        setCurrentBoundingBoxes,
        assignDefaultBoundingBox,
        makeCurrentBoundingBoxesActive
    } = useContext(AnnotationFieldsStateContext)
    const currentAnnotations = currentAnnotationsContext as
        | AnnotationFieldContent[]
        | undefined

    const [noValue] = useBoolean(currentAnnotations?.[0]?.content === null)
    const [isShowSubmit, showSubmit] = useBoolean(false)
    const [textChanged, setTextChanged] = useBoolean(false)
    const [errors, setErrors] = useState<boolean[]>()

    useEffect(() => {
        setValid(status === BoundingBoxStatus.TRUSTED)
    }, [status, setValid])

    // assign BB on mount, only for new field
    useEffect(() => {
        if (status === BoundingBoxStatus.ACTIVE && !currentAnnotations) {
            assignDefaultBoundingBox()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAnnotations, assignDefaultBoundingBox])

    const isApproved = status === BoundingBoxStatus.TRUSTED
    const isConfirmation = status === BoundingBoxStatus.NON_TRUSTED
    const isActive = status === BoundingBoxStatus.ACTIVE

    const validateContent = (isFormValidation: boolean = true) => {
        if (noValue) {
            return true
        }
        if (rootSchema && contentSchema) {
            const validationResult = currentAnnotations?.map(({ content }) =>
                isValid(
                    contentSchema,
                    getFormDataForAnnotationContent(contentSchema, content),
                    rootSchema
                )
            )
            if (validationResult?.every(result => result)) {
                setErrors(undefined)
                if (isFormValidation) {
                    showSubmit()
                }
                return true
            } else {
                setErrors(validationResult?.map(isValid => !isValid))
                return false
            }
        }
        return false
    }

    useEffect(() => {
        setValidation(() => validateContent)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAnnotations, setValidation])

    const handleChangeContent = (index: number) => (value: unknown) => {
        if (isApproved) {
            makeCurrentBoundingBoxesActive()
        }
        if (currentAnnotations) {
            setTextChanged()
            const newAnnotations = [...currentAnnotations]
            newAnnotations[index] = {
                ...newAnnotations[index],
                content: value as string
            }
            setCurrentAnnotations(newAnnotations)
        }
    }

    const handleSubmit = () => {
        const exportedBoundingBoxes =
            rendererHelpers?.exportInitialScaleValue() as DocedBoxFeature[]
        if (exportedBoundingBoxes && rootSchema && contentSchema) {
            const isValid = validateContent(false)
            if (isValid) {
                onSubmit(
                    exportedBoundingBoxes.map(
                        ({ pageNumber, rect: boundingBox }, index) => ({
                            pageNumber,
                            boundingBox,
                            content: noValue
                                ? null
                                : getFormDataForAnnotationContent(
                                      contentSchema,
                                      currentAnnotations?.[index].content
                                  )
                        })
                    )
                )
                if (currentBoundingBoxes) {
                    setCurrentBoundingBoxes({
                        ...currentBoundingBoxes,
                        boundingBoxes: exportedBoundingBoxes.map(
                            boundingBox => ({
                                ...boundingBox,
                                style: BoundingBoxStatusStyles[
                                    BoundingBoxStatus.TRUSTED
                                ]
                            })
                        )
                    })
                }
                rendererHelpers?.selectInstances([])
                setStatus(BoundingBoxStatus.TRUSTED)
                setErrors(undefined)
            }
        }
    }

    // TODO: no value implementation, commented until no value support
    // const handleValueChange = () => {
    //     if (isApproved) {
    //         makeCurrentBoundingBoxesActive()
    //         if (currentAnnotations) {
    //             setCurrentAnnotations(
    //                 currentAnnotations.map(annotation => ({
    //                     ...annotation,
    //                     content: annotation.content ?? undefined
    //                 }))
    //             )
    //         }
    //     }
    //     toggle()
    // }

    return (
        <div className="bg-marketplace-bg-grey -mx-4 mt-4 px-4 py-3">
            <InstructionsHeader
                title={
                    <>
                        {status === BoundingBoxStatus.ACTIVE && (
                            <span>
                                Select the{" "}
                                <span
                                    className={`text-${ACTIVE_STYLE.classname}`}
                                >
                                    outline of your field
                                </span>
                            </span>
                        )}
                        {status === BoundingBoxStatus.NON_TRUSTED && (
                            <span>
                                Confirm or edit the{" "}
                                <span
                                    className={`text-${NON_TRUSTED_STYLE.classname}`}
                                >
                                    outline of the field
                                </span>
                            </span>
                        )}
                        {status === BoundingBoxStatus.TRUSTED && (
                            <span>Field definition</span>
                        )}
                    </>
                }
            />
            {currentAnnotations?.map((annotation, index) => (
                <div className="mb-4" key={`${index}`}>
                    <ContentInput
                        readOnly={noValue}
                        schema={contentSchema}
                        value={
                            getFormDataForAnnotationContent(
                                contentSchema,
                                annotation.content || ""
                            ) as string
                        }
                        onChange={handleChangeContent(index)}
                        placeholder={
                            isApproved
                                ? ""
                                : "Highlight the value on your document"
                        }
                        className={`${
                            isApproved ? "border-solid border-black" : ""
                        } ${
                            isConfirmation
                                ? `border-dashed border-${NON_TRUSTED_STYLE.classname}`
                                : ""
                        } ${
                            isActive
                                ? `border-dashed border-${ACTIVE_STYLE.classname}`
                                : ""
                        } ${noValue ? "opacity-25" : ""}`}
                    />
                    {errors?.[index] && (
                        <ValidationError>Invalid content</ValidationError>
                    )}
                </div>
            ))}
            {/* 
                TODO: no value implementation, commented until no value support
            <Checkbox
                interactive
                variant="modern"
                isChecked={noValue}
                onChange={handleValueChange}
                className="inline-flex flex-grow px-2 my-0"
                label="No value (to get predictions you need to add a value at some point)"
            /> */}
            {!isApproved && (
                <div className="flex justify-end">
                    <OutlinedButton
                        disabled={!noValue && !changed && !textChanged}
                        Icon={CheckIcon}
                        label="Submit"
                        marginLeft="2"
                        className={`text-white ${
                            isShowSubmit ? "bg-grape-500" : "bg-black"
                        }`}
                        disabledClassName="bg-gray-300 opacity-50 cursor-not-allowed"
                        onClick={handleSubmit}
                    />
                </div>
            )}
        </div>
    )
}
